import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { FiUsers, FiCheck, FiHelpCircle, FiClock, FiTrendingUp, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { SettingsHeader, SettingsActions, RecordBanner, useSettingsSub } from './ClientSettings';
import { PROVIDERS, RECENT_SYNCS, HOLDERS, useClient } from './clientSettingsData';

interface Form {
    provider: string;
    clientId: string;
    apiKey: string;
    secret: string;
    nightly: boolean;
    morning: boolean;
    reportEmails: boolean;
    newHolderMin: string;
    largeMovement: string;
}

const INITIAL: Form = {
    provider: 'Automic',
    clientId: '49M‑AUT‑00417',
    apiKey: 'ak_live_7f3c9e21b8d44a06a1f5',
    secret: 'cs_4be81d0f2a9c',
    nightly: true,
    morning: false,
    reportEmails: true,
    newHolderMin: 'A$ 10,000',
    largeMovement: '1,000,000 shares',
};

// sync preview: the four steps light up as the (simulated) holder count climbs
const STEPS: { label: string; doneAt: number }[] = [
    { label: 'Connected', doneAt: 0.02 },
    { label: 'Holders', doneAt: 0.75 },
    { label: 'Movements', doneAt: 0.92 },
    { label: 'Matching sign‑ups', doneAt: 1 },
];
const SYNC_SECONDS = 24;

const Secret: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const [shown, setShown] = useState(false);
    return <div className="value-box input-box">
        <input type={shown ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} autoComplete="off" />
        <button type="button" className="link" onClick={() => setShown(!shown)}>{shown ? 'Hide' : 'Show'}</button>
    </div>;
};

const RegistryConnection: React.FC = () => {
    const history = useHistory();
    const { ticker } = useClient();
    const sub = useSettingsSub();
    const [form, setForm] = useState<Form>(INITIAL);
    const [saved, setSaved] = useState<Form>(INITIAL);
    const [lastSync, setLastSync] = useState({ completed: '02 Sep 2026, 6:10 pm', holders: `${HOLDERS} (+3)`, movements: '20', duration: '41 seconds' });
    const [syncs, setSyncs] = useState(RECENT_SYNCS);
    // 0–1 while a sync runs, null otherwise
    const [progress, setProgress] = useState<number | null>(null);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const set = <K extends keyof Form>(field: K, value: Form[K]) => setForm(f => ({ ...f, [field]: value }));

    useEffect(() => {
        if (progress === null) return;
        if (progress >= 1) {
            const now = moment();
            setLastSync({ completed: now.format('DD MMM YYYY, h:mm a'), holders: `${HOLDERS} (+0)`, movements: '0', duration: `${SYNC_SECONDS} seconds` });
            setSyncs(h => [{ when: now.format('DD MMM · h:mm a'), ok: true, note: 'OK' }, ...h].slice(0, 5));
            setProgress(null);
            toast.success(`Sync finished · ${HOLDERS} holders (preview: nothing was read from ${form.provider})`);
            return;
        }
        const t = setTimeout(() => setProgress(p => p === null ? null : Math.min(1, p + 0.25 / SYNC_SECONDS)), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress]);

    const save = () => {
        setSaved(form);
        toast.info('Preview only: this isn’t connected yet, so the registry settings were not saved');
    };

    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };
    const holdersDone = progress === null ? 0 : Math.round(HOLDERS * Math.min(1, progress / STEPS[1].doneAt));
    const secondsLeft = progress === null ? 0 : Math.ceil((1 - progress) * SYNC_SECONDS);

    return <>
        <SettingsHeader title="Registry Connection" sub={sub} {...actions} />

        {progress !== null && <div className="sync-banner">
            <div className="head">
                <span className="icon-tile"><FiRefreshCw className="spin" /></span>
                <div className="text">
                    <div className="title">Pulling today's register from {form.provider}…</div>
                    <div className="hint">{holdersDone} of {HOLDERS} holders · about {secondsLeft} seconds left. You can keep working — we'll tell you when it's done.</div>
                </div>
                <button type="button" className="cancel" onClick={() => { setProgress(null); toast.info('Sync cancelled'); }}>Cancel</button>
            </div>
            <div className="bar"><span style={{ width: `${progress * 100}%` }} /></div>
            <div className="steps">
                {STEPS.map(s => <span key={s.label} className={progress >= s.doneAt ? 'done' : ''}>
                    {progress >= s.doneAt ? <FiCheck /> : <FiClock />} {s.label}
                </span>)}
            </div>
        </div>}

        <RecordBanner />

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUsers /></span>
                        <div className="text">
                            <h2>Share Registry Provider</h2>
                            <div className="hint">Investi reads the register nightly from your registry's API</div>
                        </div>
                        <span className="badge ok">Connected</span>
                    </div>

                    <div className="options four" role="radiogroup" aria-label="Share registry provider">
                        {PROVIDERS.map(p => <button key={p} type="button" role="radio" aria-checked={form.provider === p}
                            className={`option${form.provider === p ? ' selected' : ''}`} onClick={() => set('provider', p)}>
                            <span className="radio" /> <strong>{p}</strong>
                        </button>)}
                    </div>

                    <div className="fields">
                        <label className="field">
                            <span className="label">Client ID</span>
                            <input value={form.clientId} onChange={e => set('clientId', e.target.value)} />
                        </label>
                        <div className="field">
                            <span className="label">API key</span>
                            <Secret value={form.apiKey} onChange={v => set('apiKey', v)} />
                        </div>
                        <div className="field">
                            <span className="label">Client secret</span>
                            <Secret value={form.secret} onChange={v => set('secret', v)} />
                        </div>
                        <div className="field">
                            <span className="label">Issuer code at registry</span>
                            <div className="value-box subtle"><span>{ticker}</span></div>
                        </div>
                    </div>

                    <div className="buttons">
                        <button type="button" className="btn-outline with-icon" onClick={() => toast.info(`Preview only: the connection to ${form.provider} wasn’t tested`)}><FiCheck /> Test Connection</button>
                        <button type="button" className="btn-outline with-icon" onClick={() => history.push('/help')}><FiHelpCircle /> Where Do I Find These?</button>
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><h2>Sync Schedule</h2></div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Nightly sync</div>
                            <div className="hint">Every trading day at 6:00 pm AEST, after CHESS settlement</div>
                        </div>
                        <Toggle label="Nightly sync" checked={form.nightly} onChange={v => set('nightly', v)} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Also sync at 8:00 am</div>
                            <div className="hint">Catches late overnight updates before the market opens</div>
                        </div>
                        <Toggle label="Also sync at 8:00 am" checked={form.morning} onChange={v => set('morning', v)} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Movement report emails</div>
                            <div className="hint">Send the daily holdings movement report to s.taylor@49metals.com.au</div>
                        </div>
                        <Toggle label="Movement report emails" checked={form.reportEmails} onChange={v => set('reportEmails', v)} />
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiTrendingUp /></span><h2>Movement Badges</h2></div>
                    <div className="fields">
                        <label className="field">
                            <span className="label stacked">New holder minimum value <em>holdings below this don't get a NEW or RETURNING badge</em></span>
                            <input value={form.newHolderMin} onChange={e => set('newHolderMin', e.target.value)} />
                        </label>
                        <label className="field">
                            <span className="label stacked">Large movement threshold <em>highlights big buys and sells</em></span>
                            <input value={form.largeMovement} onChange={e => set('largeMovement', e.target.value)} />
                        </label>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiCheck /></span><h2>Last Sync</h2></div>
                    <div className="kv">
                        <div><span>Completed</span><strong>{lastSync.completed}</strong></div>
                        <div><span>Holders</span><strong>{lastSync.holders}</strong></div>
                        <div><span>Movements</span><strong>{lastSync.movements}</strong></div>
                        <div><span>Duration</span><strong>{lastSync.duration}</strong></div>
                        <div><span>Next sync</span><strong>{form.nightly ? 'Today, 6:00 pm' : form.morning ? 'Tomorrow, 8:00 am' : 'Not scheduled'}</strong></div>
                    </div>
                    <button type="button" className="btn-dark" onClick={() => setProgress(0)} disabled={progress !== null}>
                        <FiTrendingUp /> {progress !== null ? 'Syncing…' : 'Sync Now'}
                    </button>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><h2>Recent Syncs</h2></div>
                    <div className="sync-list">
                        {syncs.map((s, i) => <div key={i} className={s.ok ? 'ok' : 'warn'}>
                            {s.ok ? <FiCheck /> : <FiAlertCircle />}
                            <span>{s.when}</span>
                            <em>{s.note}</em>
                        </div>)}
                    </div>
                </section>
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default RegistryConnection;
